import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LenderStats, Money } from 'api-client';

const toPlainMoney = (money?: Money): Money => ({
  amount: money?.amount ?? 0,
  currencyCode: money?.currencyCode ?? 'RUB',
});

export type MyMoneyState = {
  total: {
    invested: Money;
    reservedInvestment: Money;
    depositAmount: Money;
    comebackAmount: Money;
    withdrawAmount: Money;
  };
  premiumInvestmentSum: Money;
  premiumThreshold: Money;
  loanPayment: {
    projects: number;
  };
  fundRaising: {
    projects: number;
  };
  current: {
    projects: number;
    invested: Money;
    averageInitialTerm: number;
    forecastInterestRate: number;
    incomeLeft: Money;
    incomePaid: Money;
    forecastSimpleInterestRate: number;
    forecastYearROI: number;
  };
  closed: {
    projects: number;
    invested: Money;
    averageInitialTerm: number;
    comebackAmount: Money;
    netProfit: Money;
    interestRate: number;
    simpleInterestRate: number;
    forecastInterestRate: number;
    forecastSimpleInterestRate: number;
    forecastInitialTerm: number;
    forecastYearROI: number;
    factYearROI: number;
  };
};

const initialState: MyMoneyState = {
  total: {
    invested: { amount: 0, currencyCode: 'RUB' },
    reservedInvestment: { amount: 0, currencyCode: 'RUB' },
    depositAmount: { amount: 0, currencyCode: 'RUB' },
    comebackAmount: { amount: 0, currencyCode: 'RUB' },
    withdrawAmount: { amount: 0, currencyCode: 'RUB' },
  },
  premiumInvestmentSum: { amount: 0, currencyCode: 'RUB' },
  premiumThreshold: { amount: 0, currencyCode: 'RUB' },
  loanPayment: {
    projects: 0,
  },
  fundRaising: {
    projects: 0,
  },
  current: {
    projects: 0,
    invested: { amount: 0, currencyCode: 'RUB' },
    averageInitialTerm: 0,
    forecastInterestRate: 0,
    incomeLeft: { amount: 0, currencyCode: 'RUB' },
    incomePaid: { amount: 0, currencyCode: 'RUB' },
    forecastSimpleInterestRate: 0,
    forecastYearROI: 0,
  },
  closed: {
    projects: 0,
    invested: { amount: 0, currencyCode: 'RUB' },
    averageInitialTerm: 0,
    comebackAmount: { amount: 0, currencyCode: 'RUB' },
    netProfit: { amount: 0, currencyCode: 'RUB' },
    interestRate: 0,
    simpleInterestRate: 0,
    forecastInterestRate: 0,
    forecastSimpleInterestRate: 0,
    forecastInitialTerm: 0,
    forecastYearROI: 0,
    factYearROI: 0,
  },
};

export const myMoneySlice = createSlice({
  name: 'my-money',
  initialState,
  reducers: {
    set: (state: MyMoneyState, { payload }: PayloadAction<LenderStats>) => {
      state.total.invested = toPlainMoney(payload.total.invested);
      state.total.reservedInvestment = toPlainMoney(payload.total.reservedInvestment);
      state.total.depositAmount = toPlainMoney(payload.total.depositAmount);
      state.total.comebackAmount = toPlainMoney(payload.total.comebackAmount);
      state.total.withdrawAmount = toPlainMoney(payload.total.withdrawAmount);
      state.premiumInvestmentSum = toPlainMoney(payload.premiumInvestmentSum);
      state.premiumThreshold = toPlainMoney(payload.premiumThreshold);
      state.loanPayment.projects = payload.loanPayment.projects;
      state.fundRaising.projects = payload.fundRaising.projects;
      state.current.projects = payload.current.projects;
      state.current.invested = toPlainMoney(payload.current.invested);
      state.current.averageInitialTerm = payload.current.averageInitialTerm;
      state.current.forecastInterestRate = payload.current.forecastInterestRate;
      state.current.incomeLeft = toPlainMoney(payload.current.incomeLeft);
      state.current.incomePaid = toPlainMoney(payload.current.incomePaid);
      state.current.forecastSimpleInterestRate = payload.current.forecastSimpleInterestRate;
      state.current.forecastYearROI = payload.current.forecastYearROI;
      state.closed.projects = payload.closed.projects;
      state.closed.invested = toPlainMoney(payload.closed.invested);
      state.closed.averageInitialTerm = payload.closed.averageInitialTerm;
      state.closed.comebackAmount = toPlainMoney(payload.closed.comebackAmount);
      state.closed.netProfit = toPlainMoney(payload.closed.netProfit);
      state.closed.interestRate = payload.closed.interestRate;
      state.closed.simpleInterestRate = payload.closed.simpleInterestRate;
      state.closed.forecastInitialTerm = payload.closed.forecastInitialTerm;
      state.closed.forecastInterestRate = payload.closed.forecastInterestRate;
      state.closed.forecastSimpleInterestRate = payload.closed.forecastSimpleInterestRate;
      state.closed.forecastYearROI = payload.closed.forecastYearROI;
      state.closed.factYearROI = payload.closed.factYearROI;
    },
  },
});

export const { set } = myMoneySlice.actions;

export default myMoneySlice.reducer;
